// Costruire un Intl.DateTimeFormat costa ordini di grandezza piu' che usarlo:
// il calendario converte centinaia di orari a ogni render, quindi i formatter
// si creano una volta per fuso e si riusano. Un fuso non valido fa lanciare il
// costruttore: in quel caso non si memorizza nulla e il chiamante ricade sul
// suo catch, come prima.
const formatterCache = new Map();
function getFormatter(timeZone, withSeconds) {
  const key = `${timeZone}|${withSeconds ? "s" : ""}`;
  let formatter = formatterCache.get(key);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      ...(withSeconds ? { second: "2-digit" } : {}),
      hourCycle: "h23",
    });
    formatterCache.set(key, formatter);
  }
  return formatter;
}

/**
 * Convert an ISO instant to a YYYY-MM-DD / HH:mm pair in a target IANA zone.
 * Naive values are treated as already-local clock values for legacy safety.
 */
export function isoToZonedLocalParts(value, timeZone = "Europe/Rome") {
  if (!value) return { date: "", time: "" };
  if (!/[zZ]|[+-]\d{2}:?\d{2}$/.test(value)) {
    return { date: value.slice(0, 10), time: value.slice(11, 16) };
  }
  try {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return { date: "", time: "" };
    const parts = getFormatter(timeZone, false).formatToParts(date);
    const get = type => parts.find(part => part.type === type)?.value || "";
    return {
      date: `${get("year")}-${get("month")}-${get("day")}`,
      time: `${get("hour")}:${get("minute")}`,
    };
  } catch {
    return { date: value.slice(0, 10), time: value.slice(11, 16) };
  }
}

/** Convert a local YYYY-MM-DD + HH:mm in an IANA zone to a UTC ISO instant. */
export function zonedLocalToUtcIso(dateValue, timeValue, timeZone = "Europe/Rome") {
  if (!dateValue || !timeValue) return "";
  const match = `${dateValue}T${timeValue}`.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) return "";
  const [, yRaw, moRaw, dRaw, hRaw, miRaw] = match;
  const y = Number(yRaw);
  const mo = Number(moRaw);
  const d = Number(dRaw);
  const h = Number(hRaw);
  const mi = Number(miRaw);
  const desiredUtc = Date.UTC(y, mo - 1, d, h, mi, 0, 0);
  try {
    const formatter = getFormatter(timeZone, true);
    const offsetAt = timestamp => {
      const parts = formatter.formatToParts(new Date(timestamp));
      const get = type => Number(parts.find(part => part.type === type)?.value || 0);
      const representedAsUtc = Date.UTC(
        get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"),
      );
      return representedAsUtc - timestamp;
    };
    let actual = desiredUtc - offsetAt(desiredUtc);
    actual = desiredUtc - offsetAt(actual);
    return new Date(actual).toISOString();
  } catch {
    return new Date(desiredUtc).toISOString();
  }
}

export function isoToLocalInput(value, timeZone = "Europe/Rome") {
  const parts = isoToZonedLocalParts(value, timeZone);
  return parts.date && parts.time ? `${parts.date}T${parts.time}` : "";
}
